import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/util';
import { SpecialOccasionTypeEnum } from '@pxtra-client-lib-legacy/gen';

export const useListBonuses = (companyId?: string, options = {}) => {
  return useQuery({
    queryKey: ['bonuses', companyId],
    queryFn: () => {
      const res = apiClient.bonus.bonusList({
        company: companyId
      });
      return res;
    },
    enabled: !!companyId,
    ...options
  });
};

export const useGetBonus = (bonusId?: string, options = {}) => {
  return useQuery({
    queryKey: ['bonus', bonusId],
    queryFn: () => {
      if (!bonusId) {
        throw new Error('Bonus ID is required');
      }
      const res = apiClient.bonus.bonusRetrieve({
        id: bonusId
      });
      return res;
    },
    ...options
  });
};

export const useAvailableTaxTreatments = (companyId: string) => {
  return useQuery({
    queryKey: ['availableTaxTreatments', companyId],
    queryFn: async () => {
      const result = await apiClient.bonus.bonusAvailableTaxTreatmentsRetrieve({
        company: companyId
      });
      return result.available_tax_treatments;
    }
  });
};

export const useRetroSpecialOccasionCount = (
  params: {
    specialOccasionType?: string | null;
    employeeIds: string[];
    anniversaryInterval?: number | null;
    enabled?: boolean;
  },
  options = {}
) => {
  const isRetroType =
    params.specialOccasionType === 'BIRTHDAY' || params.specialOccasionType === 'ANNIVERSARY';
  const enabled = (params.enabled ?? true) && isRetroType && params.employeeIds.length > 0;
  return useQuery({
    queryKey: [
      'retroSpecialOccasionCount',
      params.specialOccasionType,
      params.employeeIds,
      params.anniversaryInterval
    ],
    queryFn: () =>
      apiClient.bonus.bonusRetroSpecialOccasionCountCreate({
        requestBody: {
          special_occasion_type: params.specialOccasionType as SpecialOccasionTypeEnum,
          employee_ids: params.employeeIds,
          anniversary_interval: params.anniversaryInterval ?? undefined
        }
      }),
    enabled,
    ...options
  });
};

export const useListBonusMessageTemplates = (companyId?: string) => {
  return useQuery({
    queryKey: ['message_templates', companyId],
    queryFn: async () => {
      const res = await apiClient.bonus.bonusMessageTemplatesList({ company: companyId });
      return res;
    },
    select: (data) => {
      if (!data?.results) return data;

      const sortedResults = [...data.results].sort((a, b) => {
        if (a.company && !b.company) return -1;
        if (!a.company && b.company) return 1;
        return a.name.localeCompare(b.name);
      });

      return {
        ...data,
        results: sortedResults
      };
    }
  });
};
